import React, { useMemo, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  GroupingState,
  SortingState,
  VisibilityState,
  useReactTable,
} from '@tanstack/react-table';
import Button from './UI/Button';

export type DataGridProps<T extends object> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  getRowId: (row: T) => string;
  // Selection (controlled by parent)
  enableSelection?: boolean;
  selectedIds?: string[];
  onToggleRow?: (id: string) => void;
  onToggleAll?: (allIdsOnPage: string[], checked: boolean) => void;
  // Sorting (server-side)
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (columnId: string) => void;
  // Column visibility
  initialColumnVisibility?: VisibilityState;
  // Grouping
  enableGrouping?: boolean;
  // Optional default grouping column IDs (e.g., ['name'])
  defaultGrouping?: string[];
};

// Small popover menu used for Column Chooser / Group By
const Popover: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-left">
      <Button variant="secondary" onClick={() => setOpen((o) => !o)}>
        {label}
      </Button>
      {open && (
        <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
          <div className="py-2 px-2 max-h-80 overflow-auto" onClick={(e) => e.stopPropagation()}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

function SortIndicator({ active, desc }: { active: boolean; desc: boolean }) {
  if (!active) return null;
  return (
    <span className="ml-1 inline-block align-middle">
      {desc ? (
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M14.707 7.293a1 1 0 00-1.414 0L10 10.586 6.707 7.293A1 1 0 105.293 8.707l4 4a1 1 0 001.414 0l4-4a1 1 0 000-1.414z"/></svg>
      ) : (
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M5.293 12.707a1 1 0 001.414 0L10 9.414l3.293 3.293a1 1 0 001.414-1.414l-4-4a1 1 0 00-1.414 0l-4 4a1 1 0 000 1.414z"/></svg>
      )}
    </span>
  );
}

export default function DataGrid<T extends object>(props: DataGridProps<T>) {
  const {
    data,
    columns,
    getRowId,
    enableSelection,
    selectedIds = [],
    onToggleRow,
    onToggleAll,
    sortBy,
    sortOrder,
    onSortChange,
    initialColumnVisibility,
    enableGrouping = true,
  } = props;

  const [grouping, setGrouping] = useState<GroupingState>(props.defaultGrouping || []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility || {});
  const [sortingState] = useState<SortingState>([]); // local (unused when server-side sorting)

  // Build a table instance for grouping/visibility; we won't use its sorting for server-mode
  const table = useReactTable({
    data,
    columns,
    state: {
      grouping,
      columnVisibility,
      sorting: sortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onGroupingChange: setGrouping,
    onColumnVisibilityChange: setColumnVisibility,
    // Manual sorting is handled externally
    manualSorting: true,
    enableGrouping,
  });

  const allIdsOnPage = useMemo(() => data.map(getRowId), [data, getRowId]);
  const allSelectedOnPage = useMemo(
    () => allIdsOnPage.length > 0 && allIdsOnPage.every((id) => selectedIds.includes(id)),
    [allIdsOnPage, selectedIds]
  );

  const columnsForChooser = table.getAllLeafColumns().filter((c) => c.id !== 'select' && c.id !== 'actions');

  return (
    <div className="space-y-3">
      {/* Toolbar: Column chooser + Group by */}
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex items-center space-x-2">
          {enableSelection && (
            <div className="text-sm text-gray-600">{selectedIds.length} selected</div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Group by */}
          {enableGrouping && (
            <Popover label="Group by">
              <div className="space-y-1">
                {columnsForChooser.map((col) => (
                  <label key={col.id} className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4"
                      checked={grouping.includes(col.id)}
                      onChange={(e) => {
                        setGrouping((g) =>
                          e.target.checked ? [...g, col.id] : g.filter((x) => x !== col.id)
                        );
                      }}
                    />
                    <span className="text-sm text-gray-700">{col.columnDef.header as any ?? col.id}</span>
                  </label>
                ))}
              </div>
            </Popover>
          )}

          {/* Column chooser */}
          <Popover label="Columns">
            <div className="space-y-1">
              {columnsForChooser.map((col) => (
                <label key={col.id} className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4"
                    checked={col.getIsVisible()}
                    onChange={(e) => col.toggleVisibility(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">{col.columnDef.header as any ?? col.id}</span>
                </label>
              ))}
            </div>
          </Popover>
        </div>
      </div>

      {/* Grouping chips */}
      {enableGrouping && grouping.length > 0 && (
        <div className="px-4">
          <div className="flex flex-wrap items-center gap-2">
            {grouping.map((g) => (
              <span key={g} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                Grouped by: {table.getColumn(g)?.columnDef.header as any ?? g}
                <button
                  className="ml-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setGrouping((prev) => prev.filter((x) => x !== g))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {enableSelection && (
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={allSelectedOnPage}
                      onChange={(e) => onToggleAll?.(allIdsOnPage, e.target.checked)}
                    />
                  </th>
                )}
                {headerGroup.headers.map((header) => {
                  const colId = header.column.id;
                  const sortable = (header.column.columnDef.meta as any)?.sortable;
                  const isActive = sortBy === colId;
                  const isDesc = sortOrder === 'desc';
                  return (
                    <th
                      key={header.id}
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                      }`}
                      onClick={() => sortable && onSortChange?.(colId)}
                    >
                      <div className="flex items-center">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sortable && <SortIndicator active={isActive} desc={isDesc} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => {
              const id = getRowId(row.original);
              const isSelected = selectedIds.includes(id);
              const isGrouped = row.getIsGrouped();
              return (
                <tr key={row.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''}`}>
                  {enableSelection && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      {!isGrouped && (
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={isSelected}
                          onChange={() => onToggleRow?.(id)}
                        />
                      )}
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    if (cell.getIsGrouped()) {
                      return (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" colSpan={cell.row.getVisibleCells().length}>
                          <button className="mr-2 text-gray-600" onClick={row.getToggleExpandedHandler()}>
                            {row.getIsExpanded() ? '−' : '+'}
                          </button>
                          <span className="font-medium">{cell.column.columnDef.header as any}: </span>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())} ({row.subRows.length})
                        </td>
                      );
                    }
                    if (cell.getIsPlaceholder()) return <td key={cell.id} />;
                    return (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

